import { MAX_UPLOAD_BYTES, MAX_UPLOAD_MEGABYTES } from '../../shared/uploads';
import {
  asDesignAssetError, catalogAssetCacheKey, catalogAssetMetadata, resolveCatalogAsset, subscribeCatalogAssetInvalidation,
} from '../api/assets';
import { logPhotoFlow } from '../diagnostics/photoFlow';
import { applyLegacyWhiteRemoval, removeArtworkBackground, type ArtworkBackgroundRemovalResult } from './backgroundRemoval';

const SUPPORTED = ['image/jpeg', 'image/png', 'image/webp'];
const HEADER_SCAN_BYTES = 256 * 1024;
const MAX_SOURCE_PIXELS = 100_000_000;
export { MAX_UPLOAD_BYTES, MAX_UPLOAD_MEGABYTES } from '../../shared/uploads';

export type ImagePrepareErrorCode = 'unsupported_image' | 'decode_failed' | 'heic_conversion_failed' | 'image_bitmap_failed'
  | 'canvas_init_failed' | 'image_too_large' | 'memory_pressure' | 'photo_read_failed' | 'unknown_editor_prepare_error';
export type ImagePrepareStage = 'source_read' | 'validate' | 'heic_conversion' | 'bitmap_decode' | 'image_decode' | 'resize' | 'canvas_init' | 'photo_load' | 'design_load' | 'unknown';

export interface PhotoDecodeDiagnostic {
  stage: 'decode';
  mime: string;
  detectedMime: string;
  bytes: number;
  sourceWidth?: number;
  sourceHeight?: number;
  decodeStrategy: 'bitmap_resized' | 'bitmap' | 'object_url' | 'pipeline';
  retry: 0 | 1;
  outcome: 'ok' | 'failed' | 'retrying' | 'cancelled';
  errorClass?: string;
}

export interface PhotoSourceReadDiagnostic {
  stage: 'source_read';
  mime: string;
  bytes: number;
  attempt: number;
  outcome: 'retrying' | 'retry_succeeded' | 'failed';
  errorClass: string;
}

export interface PhotoDecodeOptions {
  onDiagnostic?: (diagnostic: PhotoDecodeDiagnostic) => void;
  onSourceReadDiagnostic?: (diagnostic: PhotoSourceReadDiagnostic) => void;
  shouldContinue?: () => boolean;
  photoId?: string;
}

export type DecodedImageSource = ImageBitmap | HTMLImageElement | HTMLCanvasElement;

export class ImagePrepareError extends Error {
  readonly attempt?: number;

  constructor(
    public readonly code: ImagePrepareErrorCode,
    public readonly stage: ImagePrepareStage,
    public readonly dimensions?: { width: number; height: number },
    options?: ErrorOptions & { attempt?: number },
  ) { super(code, options); this.name = 'ImagePrepareError'; this.attempt = options?.attempt; }
}

export class ImageUploadError extends Error {
  constructor(public readonly code: 'too_large' | 'invalid_image') {
    super(code === 'too_large'
      ? `Choose an image no larger than ${MAX_UPLOAD_MEGABYTES} MB.`
      : 'Use a valid JPG, PNG, WebP, HEIC or HEIF image.');
  }
}

export function imageUploadErrorKey(error: unknown): 'api.fileTooLarge' | 'api.invalidImage' | 'api.photoReadFailed' {
  if (error instanceof ImagePrepareError && error.code === 'photo_read_failed') return 'api.photoReadFailed';
  return (error instanceof ImageUploadError && error.code === 'too_large') || (error instanceof ImagePrepareError && error.code === 'image_too_large') ? 'api.fileTooLarge' : 'api.invalidImage';
}

const memoryFailure = (error: unknown): boolean => error instanceof RangeError
  || (error instanceof Error && /out of memory|memory pressure|allocation|not enough memory|canvas.*size/i.test(error.message));

function errorClassName(error: unknown): string {
  if (error && typeof error === 'object' && 'name' in error && typeof error.name === 'string') return error.name.slice(0, 80);
  return typeof error;
}

function prepareError(error: unknown, code: ImagePrepareErrorCode, stage: ImagePrepareStage, dimensions?: { width: number; height: number }): ImagePrepareError {
  if (error instanceof ImagePrepareError) return error;
  return new ImagePrepareError(memoryFailure(error) ? 'memory_pressure' : code, stage, dimensions, { cause: error });
}

export function imagePrepareDiagnostic(error: unknown, file: File): {
  code: ImagePrepareErrorCode; stage: ImagePrepareStage; mime: string; bytes: number; errorClass: string; attempt?: number; pixelWidth?: number; pixelHeight?: number;
} {
  const typed = error instanceof ImagePrepareError ? error : error instanceof ImageUploadError
    ? new ImagePrepareError(error.code === 'too_large' ? 'image_too_large' : 'unsupported_image', 'validate', undefined, { cause: error })
    : prepareError(error, 'unknown_editor_prepare_error', 'unknown');
  const mime = /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/i.test(file.type.trim()) ? file.type.trim().toLowerCase().slice(0, 80) : 'unknown';
  const underlying = typed.cause ?? error;
  return {
    code: typed.code, stage: typed.stage, mime, bytes: Math.max(0, Number(file.size) || 0),
    errorClass: errorClassName(underlying),
    ...(typed.attempt ? { attempt: typed.attempt } : {}),
    ...(typed.dimensions ? { pixelWidth: typed.dimensions.width, pixelHeight: typed.dimensions.height } : {}),
  };
}

export function photoPipelineDiagnostic(error: unknown, file: File): ReturnType<typeof imagePrepareDiagnostic> {
  const value = imagePrepareDiagnostic(error, file);
  const code = value.code === 'decode_failed' || value.code === 'image_bitmap_failed' ? 'photo_decode_failed'
    : value.stage === 'resize' ? 'photo_normalize_failed'
      : value.code === 'unknown_editor_prepare_error' && value.stage === 'photo_load' ? 'editor_photo_load_failed'
        : value.code;
  return { ...value, code } as ReturnType<typeof imagePrepareDiagnostic>;
}

function heifBrand(bytes: Uint8Array): boolean {
  if (bytes.length < 12 || String.fromCharCode(...bytes.slice(4, 8)) !== 'ftyp') return false;
  return ['heic', 'heif', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'mif1', 'msf1'].includes(String.fromCharCode(...bytes.slice(8, 12)).toLowerCase());
}

async function blobArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => reader.result instanceof ArrayBuffer ? resolve(reader.result) : reject(new DOMException('The photo could not be read.', 'NotReadableError'));
    reader.onerror = () => reject(reader.error || new DOMException('The photo could not be read.', 'NotReadableError'));
    reader.readAsArrayBuffer(blob);
  });
}

export async function isHeicFile(file: File): Promise<boolean> {
  return (await readSourceImageBytes(file)).inspection.mime === 'image/heic';
}

export async function validateImageFile(file: File): Promise<string> {
  return (await readSourceImageBytes(file)).inspection.mime;
}

export async function browserImageFile(file: File): Promise<File> {
  return (await canonicalBrowserImage(file)).file;
}

export interface ImageInspection { mime: string; width: number | null; height: number | null; orientation: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 }

function uint16(bytes: Uint8Array, offset: number, little = false): number {
  return little ? bytes[offset] | bytes[offset + 1] << 8 : bytes[offset] << 8 | bytes[offset + 1];
}

function uint32(bytes: Uint8Array, offset: number, little = false): number {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength).getUint32(offset, little);
}

function jpegInspection(bytes: Uint8Array): Pick<ImageInspection, 'width' | 'height' | 'orientation'> {
  let width: number | null = null;
  let height: number | null = null;
  let orientation: ImageInspection['orientation'] = 1;
  let offset = 2;
  try {
    while (offset + 4 <= bytes.length) {
      if (bytes[offset] !== 0xff) { offset += 1; continue; }
      while (bytes[offset] === 0xff) offset += 1;
      const marker = bytes[offset++];
      if (marker === 0xd8 || marker === 0xd9 || marker === 0xda) continue;
      if (offset + 2 > bytes.length) break;
      const length = uint16(bytes, offset);
      if (length < 2 || offset + length > bytes.length) break;
      const data = offset + 2;
      if ([0xc0, 0xc1, 0xc2, 0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf].includes(marker) && data + 5 <= bytes.length) {
        height = uint16(bytes, data + 1); width = uint16(bytes, data + 3);
      }
      if (marker === 0xe1 && data + 14 <= bytes.length && String.fromCharCode(...bytes.slice(data, data + 6)) === 'Exif\0\0') {
        const tiff = data + 6;
        const little = String.fromCharCode(bytes[tiff], bytes[tiff + 1]) === 'II';
        const validEndian = little || String.fromCharCode(bytes[tiff], bytes[tiff + 1]) === 'MM';
        if (validEndian) {
          const ifd = tiff + uint32(bytes, tiff + 4, little);
          const entries = ifd + 2 <= bytes.length ? uint16(bytes, ifd, little) : 0;
          for (let index = 0; index < entries; index += 1) {
            const entry = ifd + 2 + index * 12;
            if (entry + 12 > bytes.length) break;
            if (uint16(bytes, entry, little) !== 0x0112) continue;
            const value = uint16(bytes, entry + 8, little);
            if (value >= 1 && value <= 8) orientation = value as ImageInspection['orientation'];
            break;
          }
        }
      }
      offset += length;
      if (width && height && orientation !== 1) break;
    }
  } catch { /* Truncated metadata is allowed; the decoder remains authoritative. */ }
  return { width, height, orientation };
}

function webpDimensions(bytes: Uint8Array): { width: number | null; height: number | null } {
  const chunk = String.fromCharCode(...bytes.slice(12, 16));
  if (chunk === 'VP8X' && bytes.length >= 30) return {
    width: 1 + bytes[24] + (bytes[25] << 8) + (bytes[26] << 16),
    height: 1 + bytes[27] + (bytes[28] << 8) + (bytes[29] << 16),
  };
  if (chunk === 'VP8L' && bytes.length >= 25 && bytes[20] === 0x2f) return {
    width: 1 + bytes[21] + ((bytes[22] & 0x3f) << 8),
    height: 1 + (bytes[22] >> 6) + (bytes[23] << 2) + ((bytes[24] & 0x0f) << 10),
  };
  if (chunk === 'VP8 ' && bytes.length >= 30 && bytes[23] === 0x9d && bytes[24] === 0x01 && bytes[25] === 0x2a) return {
    width: uint16(bytes, 26, true) & 0x3fff, height: uint16(bytes, 28, true) & 0x3fff,
  };
  return { width: null, height: null };
}

function detectedImageMime(bytes: Uint8Array): string {
  let detected = '';
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) detected = 'image/jpeg';
  if ([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((value, index) => bytes[index] === value)) detected = 'image/png';
  if (String.fromCharCode(...bytes.slice(0, 4)) === 'RIFF' && String.fromCharCode(...bytes.slice(8, 12)) === 'WEBP') detected = 'image/webp';
  if (!detected && heifBrand(bytes)) detected = 'image/heic';
  if (!SUPPORTED.includes(detected) && detected !== 'image/heic') throw new ImageUploadError('invalid_image');
  return detected;
}

function inspectImageBytes(allBytes: Uint8Array): ImageInspection {
  if (allBytes.length < 16) throw new ImageUploadError('invalid_image');
  const mime = detectedImageMime(allBytes);
  const bytes = allBytes.subarray(0, Math.min(allBytes.length, HEADER_SCAN_BYTES));
  let width: number | null = null;
  let height: number | null = null;
  let orientation: ImageInspection['orientation'] = 1;
  if (mime === 'image/jpeg') ({ width, height, orientation } = jpegInspection(bytes));
  else if (mime === 'image/png' && bytes.length >= 24) { width = uint32(bytes, 16); height = uint32(bytes, 20); }
  else if (mime === 'image/webp') ({ width, height } = webpDimensions(bytes));
  if (width && height && (width * height > MAX_SOURCE_PIXELS || width > 20_000 || height > 20_000)) {
    throw new ImagePrepareError('image_too_large', 'validate', { width, height });
  }
  return { mime, width, height, orientation };
}

function safeDeclaredMime(file: File): string {
  return /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/i.test(file.type.trim()) ? file.type.trim().toLowerCase().slice(0, 80) : 'unknown';
}

function canonicalImageName(file: File, mime: string): string {
  const extension = mime === 'image/jpeg' ? 'jpg' : mime === 'image/heic' ? 'heic' : mime.split('/')[1];
  const inputName = file.name || 'walld-photo';
  return /\.[^.]+$/.test(inputName) ? inputName.replace(/\.[^.]+$/, `.${extension}`) : `${inputName}.${extension}`;
}

function transientPhotoReadFailure(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const name = 'name' in error && typeof error.name === 'string' ? error.name : '';
  const message = 'message' in error && typeof error.message === 'string' ? error.message : '';
  if (['NotReadableError', 'NotFoundError', 'AbortError'].includes(name)) return true;
  return /not.?readable|could not be read|failed to read|i\/o error/i.test(message);
}

function sourceReadDiagnostic(file: File, attempt: number, outcome: PhotoSourceReadDiagnostic['outcome'], error: unknown,
  detectedMime?: string): PhotoSourceReadDiagnostic {
  return {
    stage: 'source_read', mime: detectedMime || safeDeclaredMime(file), bytes: Math.max(0, Number(file.size) || 0), attempt, outcome,
    errorClass: errorClassName(error),
  };
}

export interface StableImageSource {
  file: File;
  inspection: ImageInspection;
  sourceReadAttempts: number;
}

export async function readSourceImageBytes(file: File, options: PhotoDecodeOptions = {}): Promise<StableImageSource> {
  if (!file || file.size < 16) throw new ImageUploadError('invalid_image');
  if (file.size > MAX_UPLOAD_BYTES) throw new ImageUploadError('too_large');
  const delays = [0, 75, 200] as const;
  let previousFailure: unknown;
  for (let index = 0; index < delays.length; index += 1) {
    const attempt = index + 1;
    if (options.shouldContinue && !options.shouldContinue()) throw decodeAbortError();
    if (delays[index]) await new Promise<void>((resolve) => window.setTimeout(resolve, delays[index]));
    if (options.shouldContinue && !options.shouldContinue()) throw decodeAbortError();
    try {
      const bytes = new Uint8Array(await blobArrayBuffer(file));
      const inspection = inspectImageBytes(bytes);
      const stable = new File([bytes], canonicalImageName(file, inspection.mime), {
        type: inspection.mime,
        lastModified: file.lastModified || Date.now(),
      });
      if (attempt > 1) {
        const diagnostic = sourceReadDiagnostic(file, attempt, 'retry_succeeded', previousFailure, inspection.mime);
        options.onSourceReadDiagnostic?.(diagnostic);
        logPhotoFlow('PHOTO_READ_RETRY_SUCCESS', { photoId: options.photoId, attempt, mime: inspection.mime, bytes: file.size, errorClass: diagnostic.errorClass });
      }
      return { file: stable, inspection, sourceReadAttempts: attempt };
    } catch (error) {
      if (error instanceof ImageUploadError || error instanceof ImagePrepareError) throw error;
      if (memoryFailure(error)) throw prepareError(error, 'memory_pressure', 'source_read');
      previousFailure = error;
      const retry = transientPhotoReadFailure(error) && attempt < delays.length;
      options.onSourceReadDiagnostic?.(sourceReadDiagnostic(file, attempt, retry ? 'retrying' : 'failed', error));
      if (!retry) throw new ImagePrepareError('photo_read_failed', 'source_read', undefined, { cause: error, attempt });
    }
  }
  throw new ImagePrepareError('photo_read_failed', 'source_read', undefined, { cause: previousFailure, attempt: delays.length });
}

async function canonicalBrowserImage(file: File, options: PhotoDecodeOptions = {}): Promise<StableImageSource> {
  const source = await readSourceImageBytes(file, options);
  if (source.inspection.mime !== 'image/heic') return source;
  try {
    const { heicTo } = await import('heic-to/csp');
    const jpeg = await heicTo({ blob: source.file, type: 'image/jpeg', quality: .9 });
    const stem = source.file.name.replace(/\.(?:heic|heif)$/i, '') || 'walld-photo';
    return await readSourceImageBytes(new File([jpeg], `${stem}.jpg`, {
      type: 'image/jpeg', lastModified: source.file.lastModified || Date.now(),
    }), { shouldContinue: options.shouldContinue, photoId: options.photoId });
  } catch (error) { throw prepareError(error, 'heic_conversion_failed', 'heic_conversion'); }
}

export async function inspectImageFile(file: File): Promise<ImageInspection> {
  return (await readSourceImageBytes(file)).inspection;
}

function orientedSize(inspection: ImageInspection): { width: number; height: number } | null {
  if (!inspection.width || !inspection.height) return null;
  return inspection.orientation >= 5
    ? { width: inspection.height, height: inspection.width }
    : { width: inspection.width, height: inspection.height };
}

function fittedSize(size: { width: number; height: number } | null, maxEdge?: number): { width: number; height: number } | null {
  if (!size || !maxEdge || Math.max(size.width, size.height) <= maxEdge) return null;
  const ratio = maxEdge / Math.max(size.width, size.height);
  return { width: Math.max(1, Math.round(size.width * ratio)), height: Math.max(1, Math.round(size.height * ratio)) };
}

export function releaseDecodedImage(source: DecodedImageSource): void {
  if ('close' in source && typeof source.close === 'function') source.close();
  else if (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement) { source.width = 0; source.height = 0; }
  else if ('src' in source) source.src = '';
}

function decodeAbortError(): DOMException {
  return new DOMException('The photo preparation is no longer active.', 'AbortError');
}

function diagnosticFor(file: File, inspection: ImageInspection, retry: 0 | 1, decodeStrategy: PhotoDecodeDiagnostic['decodeStrategy'],
  outcome: PhotoDecodeDiagnostic['outcome'], error?: unknown): PhotoDecodeDiagnostic {
  const mime = /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/i.test(file.type.trim()) ? file.type.trim().toLowerCase().slice(0, 80) : 'unknown';
  return {
    stage: 'decode', mime, detectedMime: inspection.mime, bytes: Math.max(0, Number(file.size) || 0),
    ...(inspection.width ? { sourceWidth: inspection.width } : {}), ...(inspection.height ? { sourceHeight: inspection.height } : {}),
    decodeStrategy, retry, outcome,
    ...(error ? { errorClass: errorClassName(error) } : {}),
  };
}

async function decodeBrowserFile(file: File, inspection: ImageInspection, maxEdge: number | undefined, retry: 0 | 1,
  options: PhotoDecodeOptions): Promise<DecodedImageSource> {
  const target = fittedSize(orientedSize(inspection), maxEdge);
  const failures: unknown[] = [];
  if ('createImageBitmap' in window) {
    const bitmapOptions: ImageBitmapOptions = { imageOrientation: 'from-image', ...(target ? { resizeWidth: target.width, resizeHeight: target.height, resizeQuality: 'high' } : {}) };
    try {
      const decoded = await createImageBitmap(file, bitmapOptions);
      options.onDiagnostic?.(diagnosticFor(file, inspection, retry, target ? 'bitmap_resized' : 'bitmap', 'ok'));
      return decoded;
    }
    catch (error) {
      failures.push(error);
      options.onDiagnostic?.(diagnosticFor(file, inspection, retry, target ? 'bitmap_resized' : 'bitmap', 'failed', error));
      if (memoryFailure(error)) throw prepareError(error, 'image_bitmap_failed', 'bitmap_decode', orientedSize(inspection) || undefined);
    }
    try {
      const decoded = await createImageBitmap(file);
      options.onDiagnostic?.(diagnosticFor(file, inspection, retry, 'bitmap', 'ok'));
      return decoded;
    }
    catch (error) {
      failures.push(error);
      options.onDiagnostic?.(diagnosticFor(file, inspection, retry, 'bitmap', 'failed', error));
    }
  }
  const url = URL.createObjectURL(file);
  logPhotoFlow('OBJECT_URL_CREATED', { photoId: options.photoId, retry });
  try {
    const image = await loadImage(url, false);
    // Own the decoded pixels before releasing the blob URL. Returning an <img>
    // whose src has already been revoked is racy in Android Telegram WebView.
    const decoded = downscale(image, maxEdge || Math.max(image.naturalWidth, image.naturalHeight));
    options.onDiagnostic?.(diagnosticFor(file, inspection, retry, 'object_url', 'ok'));
    return decoded;
  }
  catch (error) {
    failures.push(error);
    options.onDiagnostic?.(diagnosticFor(file, inspection, retry, 'object_url', 'failed', error));
    const memory = failures.find(memoryFailure);
    throw prepareError(memory || error, 'decode_failed', 'image_decode', orientedSize(inspection) || undefined);
  } finally {
    URL.revokeObjectURL(url);
    logPhotoFlow('OBJECT_URL_REVOKED', { photoId: options.photoId, retry });
  }
}

async function decodePreparedFile(file: File, inspection: ImageInspection, maxEdge: number | undefined,
  options: PhotoDecodeOptions): Promise<DecodedImageSource> {
  for (let attempt = 0; attempt <= 1; attempt += 1) {
    const retry = attempt as 0 | 1;
    if (options.shouldContinue && !options.shouldContinue()) {
      options.onDiagnostic?.(diagnosticFor(file, inspection, retry, 'pipeline', 'cancelled', decodeAbortError()));
      throw decodeAbortError();
    }
    try { return await decodeBrowserFile(file, inspection, maxEdge, retry, options); }
    catch (error) {
      const transient = error instanceof ImagePrepareError && ['decode_failed', 'image_bitmap_failed'].includes(error.code);
      if (retry === 1 || !transient) throw error;
      options.onDiagnostic?.(diagnosticFor(file, inspection, retry, 'pipeline', 'retrying', error));
      await new Promise<void>((resolve) => window.setTimeout(resolve, 32));
    }
  }
  throw new ImagePrepareError('decode_failed', 'image_decode');
}

interface NormalizedStableImage {
  file: File;
  decodedDuringNormalization: boolean;
}

async function normalizeStableImage(source: StableImageSource, maxEdge: number, quality: number,
  options: PhotoDecodeOptions): Promise<NormalizedStableImage> {
  const original = orientedSize(source.inspection);
  const requiresNormalization = Boolean(original && Math.max(original.width, original.height) > maxEdge) || source.file.size > 8 * 1024 * 1024;
  // Small supported sources are decoded exactly once by CanvasEngine. Large
  // sources are reduced here with decoder-side resize before entering it.
  if (!requiresNormalization) return { file: source.file, decodedDuringNormalization: false };
  const decoded = await decodePreparedFile(source.file, source.inspection, maxEdge, options);
  const size = sourceSize(decoded);
  if (!Number.isFinite(size.width) || !Number.isFinite(size.height) || size.width < 1 || size.height < 1) {
    releaseDecodedImage(decoded); throw new ImagePrepareError('decode_failed', 'image_decode');
  }
  let canvas: HTMLCanvasElement;
  try { canvas = consumeDecodedImage(decoded, maxEdge); }
  catch (error) { throw prepareError(error, 'canvas_init_failed', 'resize', size); }
  try {
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((value) => value ? resolve(value) : reject(new ImagePrepareError('canvas_init_failed', 'resize', size)), 'image/jpeg', quality));
    const stem = (source.file.name || 'walld-photo').replace(/\.[^.]+$/, '') || 'walld-photo';
    return { file: new File([blob], `${stem}.jpg`, { type: 'image/jpeg', lastModified: source.file.lastModified || Date.now() }), decodedDuringNormalization: true };
  } catch (error) { throw prepareError(error, 'canvas_init_failed', 'resize', size); }
  finally { canvas.width = 0; canvas.height = 0; }
}

export async function prepareImageFile(file: File, maxEdge = 2400, quality = .9, options: PhotoDecodeOptions = {}): Promise<File> {
  const source = await canonicalBrowserImage(file, options);
  return (await normalizeStableImage(source, maxEdge, quality, options)).file;
}

/**
 * Detaches a picker-backed File with one resilient full read, validates its
 * bytes once, and proves that the app-owned copy can be decoded before it is
 * persisted. No later stage reads from the transient picker handle.
 */
export async function preparePhotoImportFile(file: File, maxEdge = 2400, quality = .9,
  options: PhotoDecodeOptions = {}): Promise<File> {
  const source = await canonicalBrowserImage(file, options);
  const normalized = await normalizeStableImage(source, maxEdge, quality, options);
  if (!normalized.decodedDuringNormalization) {
    const decoded = await decodePreparedFile(normalized.file, source.inspection, maxEdge, options);
    releaseDecodedImage(decoded);
  }
  return normalized.file;
}

async function loadImage(url: string, crossOrigin = true): Promise<HTMLImageElement> {
  const image = new Image();
  if (crossOrigin) image.crossOrigin = 'anonymous';
  const loaded = new Promise<HTMLImageElement>((resolve, reject) => {
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('The image could not be decoded.'));
  });
  image.src = url;
  if (typeof image.decode === 'function') {
    try {
      await image.decode();
      if (image.naturalWidth > 0 && image.naturalHeight > 0) return image;
    } catch {
      // Some Android WebViews reject decode() while the ordinary load event
      // still succeeds. Keep the independent load path as the fallback.
    }
  }
  return loaded;
}

export async function decodeImageFile(file: File, maxEdge?: number, options: PhotoDecodeOptions = {}): Promise<DecodedImageSource> {
  const source = await canonicalBrowserImage(file, options);
  return decodePreparedFile(source.file, source.inspection, maxEdge, options);
}

export async function decodeUrl(url: string): Promise<HTMLImageElement> {
  const resolved = await resolveCatalogAsset(url);
  try { return await loadImage(resolved); }
  catch (cause) { throw asDesignAssetError(cause, 'design_decode_failed', 'decode', catalogAssetMetadata(url)); }
}

export function sourceSize(source: CanvasImageSource): { width: number; height: number } {
  const item = source as HTMLImageElement & { naturalWidth?: number; naturalHeight?: number; videoWidth?: number; videoHeight?: number };
  return {
    width: item.naturalWidth || item.videoWidth || item.width,
    height: item.naturalHeight || item.videoHeight || item.height,
  };
}

/**
 * Consumes a decoder-owned image source and returns the canvas that the next
 * pipeline stage owns. An object-URL fallback is already materialized into a
 * bounded canvas, so adopting it avoids a second full-size pixel allocation.
 * Callers must release the returned canvas when their ownership ends.
 */
export function consumeDecodedImage(source: DecodedImageSource, maxEdge: number): HTMLCanvasElement {
  if (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement) {
    const { width, height } = sourceSize(source);
    if (width > 0 && height > 0 && Math.max(width, height) <= maxEdge) return source;
    try { return downscale(source, maxEdge); }
    finally { releaseDecodedImage(source); }
  }
  return downscale(source, maxEdge);
}

export function downscale(source: CanvasImageSource, maxEdge: number): HTMLCanvasElement {
  const { width, height } = sourceSize(source);
  const ratio = Math.min(1, maxEdge / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * ratio));
  canvas.height = Math.max(1, Math.round(height * ratio));
  const context = canvas.getContext('2d');
  if (!context) {
    if ('close' in source && typeof source.close === 'function') source.close();
    throw new ImagePrepareError('canvas_init_failed', 'canvas_init', { width, height });
  }
  try { context.drawImage(source, 0, 0, canvas.width, canvas.height); }
  catch (error) { canvas.width = 0; canvas.height = 0; throw prepareError(error, 'canvas_init_failed', 'resize', { width, height }); }
  finally {
    if ('close' in source && typeof source.close === 'function') source.close();
    else if (typeof HTMLImageElement !== 'undefined' && source instanceof HTMLImageElement) source.src = '';
  }
  return canvas;
}

export interface ArtworkAnalysis {
  bounds: { x: number; y: number; width: number; height: number };
  hasTransparency: boolean;
  lightBackground: boolean;
  cropped: boolean;
}

export interface NormalizedArtwork {
  canvas: HTMLCanvasElement;
  analysis: ArtworkAnalysis;
}

export function shouldEnableWhiteRemoval(analysis: Pick<ArtworkAnalysis, 'hasTransparency' | 'lightBackground'>, explicit?: boolean): boolean {
  void analysis;
  void explicit;
  return true;
}

export function applyWhiteRemovalToPixels(
  data: Uint8ClampedArray,
  threshold: number,
  width?: number,
  height?: number,
): ArtworkBackgroundRemovalResult {
  if (width === undefined || height === undefined) {
    applyLegacyWhiteRemoval(data, threshold);
    return { mode: 'clean_light', neutralLightRatio: 0 };
  }
  return removeArtworkBackground(data, threshold, width, height);
}

const fullBounds = (width: number, height: number) => ({ x: 0, y: 0, width, height });

/** Finds visible art without modifying pixels. Opaque cropping is deliberately
 * conservative: it only runs when the perimeter is both light and uniform. */
export function analyzeArtworkPixels(data: Uint8ClampedArray, width: number, height: number): ArtworkAnalysis {
  const pixelCount = Math.max(1, width * height);
  let transparent = 0;
  for (let index = 3; index < data.length; index += 4) if (data[index] < 245) transparent += 1;
  const hasTransparency = transparent / pixelCount > .001;

  let background = { r: 255, g: 255, b: 255 };
  let lightBackground = false;
  if (!hasTransparency) {
    const samples: Array<[number, number, number]> = [];
    const stride = Math.max(1, Math.floor(Math.min(width, height) / 96));
    const add = (x: number, y: number) => {
      const index = (y * width + x) * 4;
      samples.push([data[index], data[index + 1], data[index + 2]]);
    };
    for (let x = 0; x < width; x += stride) { add(x, 0); add(x, height - 1); }
    for (let y = stride; y < height - stride; y += stride) { add(0, y); add(width - 1, y); }
    const divisor = Math.max(1, samples.length);
    background = samples.reduce((sum, sample) => ({ r: sum.r + sample[0], g: sum.g + sample[1], b: sum.b + sample[2] }), { r: 0, g: 0, b: 0 });
    background = { r: background.r / divisor, g: background.g / divisor, b: background.b / divisor };
    const variance = samples.reduce((sum, sample) => sum
      + (sample[0] - background.r) ** 2 + (sample[1] - background.g) ** 2 + (sample[2] - background.b) ** 2, 0) / (divisor * 3);
    const luminance = background.r * .2126 + background.g * .7152 + background.b * .0722;
    lightBackground = luminance >= 218 && Math.sqrt(variance) <= 18;
  }

  if (!hasTransparency && !lightBackground) return { bounds: fullBounds(width, height), hasTransparency, lightBackground, cropped: false };

  let minX = width;
  let minY = height;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const index = (y * width + x) * 4;
      const visible = hasTransparency
        ? data[index + 3] > 2
        : Math.hypot(data[index] - background.r, data[index + 1] - background.g, data[index + 2] - background.b) > 16;
      if (!visible) continue;
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }
  if (maxX < minX || maxY < minY) return { bounds: fullBounds(width, height), hasTransparency, lightBackground, cropped: false };

  const padX = Math.max(2, Math.round(width * .02));
  const padY = Math.max(2, Math.round(height * .02));
  minX = Math.max(0, minX - padX);
  minY = Math.max(0, minY - padY);
  maxX = Math.min(width - 1, maxX + padX);
  maxY = Math.min(height - 1, maxY + padY);
  const bounds = { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
  const removedFraction = 1 - (bounds.width * bounds.height) / pixelCount;
  const cropped = removedFraction >= .035;
  return { bounds: cropped ? bounds : fullBounds(width, height), hasTransparency, lightBackground, cropped };
}

export function normalizeArtwork(source: CanvasImageSource, maxEdge = 1400): NormalizedArtwork {
  const scaled = downscale(source, maxEdge);
  const context = scaled.getContext('2d', { willReadFrequently: true })!;
  let analysis: ArtworkAnalysis;
  try {
    analysis = analyzeArtworkPixels(context.getImageData(0, 0, scaled.width, scaled.height).data, scaled.width, scaled.height);
  } catch {
    analysis = { bounds: fullBounds(scaled.width, scaled.height), hasTransparency: false, lightBackground: false, cropped: false };
  }
  // The editor always receives the full source. Bounds are only metadata for
  // non-destructive library thumbnail framing.
  return { canvas: scaled, analysis };
}

export interface ArtworkThumbnailFrame {
  source: { x: number; y: number; width: number; height: number };
  destination: { x: number; y: number; width: number; height: number };
  width: number;
  height: number;
}

export function artworkThumbnailFrame(
  bounds: ArtworkAnalysis['bounds'],
  sourceWidth: number,
  sourceHeight: number,
  safeMargin = .07,
): ArtworkThumbnailFrame {
  const margin = Math.min(.2, Math.max(0, safeMargin));
  const x = Math.max(0, Math.min(sourceWidth - 1, Math.floor(bounds.x)));
  const y = Math.max(0, Math.min(sourceHeight - 1, Math.floor(bounds.y)));
  const width = Math.max(1, Math.min(sourceWidth - x, Math.ceil(bounds.width)));
  const height = Math.max(1, Math.min(sourceHeight - y, Math.ceil(bounds.height)));
  const outputWidth = Math.max(width, Math.ceil(width / Math.max(.01, 1 - margin * 2)));
  const outputHeight = Math.max(height, Math.ceil(height / Math.max(.01, 1 - margin * 2)));
  return {
    source: { x, y, width, height },
    destination: { x: Math.floor((outputWidth - width) / 2), y: Math.floor((outputHeight - height) / 2), width, height },
    width: outputWidth,
    height: outputHeight,
  };
}

const artworkCache = new Map<string, Promise<NormalizedArtwork>>();
const artworkOwners = new WeakMap<HTMLCanvasElement, Promise<NormalizedArtwork>>();
const thumbnailCache = new Map<string, Promise<string>>();
const thumbnailWaiters: Array<() => void> = [];
let activeThumbnailJobs = 0;

async function withThumbnailSlot<T>(job: () => Promise<T>): Promise<T> {
  const limit = isCoarsePointer() ? 2 : 4;
  if (activeThumbnailJobs >= limit) await new Promise<void>((resolve) => thumbnailWaiters.push(resolve));
  activeThumbnailJobs += 1;
  try { return await job(); }
  finally {
    activeThumbnailJobs -= 1;
    thumbnailWaiters.shift()?.();
  }
}

subscribeCatalogAssetInvalidation((sourceKey) => {
  artworkCache.delete(sourceKey);
  for (const key of thumbnailCache.keys()) if (key.startsWith(`${sourceKey}|`)) thumbnailCache.delete(key);
});

export function loadNormalizedArtwork(url: string): Promise<NormalizedArtwork> {
  const key = catalogAssetCacheKey(url);
  let pending = artworkCache.get(key);
  if (!pending) {
    const created = (async () => {
      const image = await decodeUrl(url);
      try { return normalizeArtwork(image); }
      catch (cause) { throw asDesignAssetError(cause, 'design_normalize_failed', 'normalize', catalogAssetMetadata(url)); }
    })();
    artworkCache.set(key, created);
    void created.then((normalized) => artworkOwners.set(normalized.canvas, created)).catch(() => undefined);
    void created.catch(() => { if (artworkCache.get(key) === created) artworkCache.delete(key); });
    pending = created;
  }
  return pending;
}

/** Prepares the complete fetch -> Blob -> decode -> normalize chain without
 * mutating editor state. The caller can commit navigation/layers afterwards. */
export async function prepareDesignAsset(url: string): Promise<void> {
  const normalized = await loadNormalizedArtwork(url);
  if (normalized.canvas.width < 1 || normalized.canvas.height < 1) {
    throw asDesignAssetError(new Error('Empty normalized artwork.'), 'design_normalize_failed', 'normalize', catalogAssetMetadata(url));
  }
}

/** Evicts the shared lookup without mutating its canvas. Consumers can overlap
 * while editors mount/unmount, so zeroing this shared backing store would make
 * a still-active layer silently render as an empty image. */
export function releaseNormalizedArtwork(url: string, canvas: HTMLCanvasElement): void {
  const key = catalogAssetCacheKey(url);
  const pending = artworkCache.get(key);
  if (!pending || artworkOwners.get(canvas) !== pending) return;
  artworkCache.delete(key);
  artworkOwners.delete(canvas);
}

export function normalizedArtworkThumbnail(url: string, maxEdge = 320, safeMargin = .07): Promise<string> {
  const key = `${catalogAssetCacheKey(url)}|${maxEdge}|${safeMargin}`;
  let pending = thumbnailCache.get(key);
  if (!pending) {
    // Library cards must not populate the full-size editor artwork cache. On
    // Android Telegram that retained one large canvas per card and could push
    // the later body-photo decode into memory pressure. Preview work is both
    // bounded and released immediately after producing the small data URL.
    const created = withThumbnailSlot(async () => {
      const image = await decodeUrl(url);
      let normalized: NormalizedArtwork;
      try { normalized = normalizeArtwork(image, Math.max(maxEdge, Math.min(640, maxEdge * 2))); }
      catch (cause) { throw asDesignAssetError(cause, 'design_normalize_failed', 'normalize', catalogAssetMetadata(url)); }
      let output: HTMLCanvasElement | null = null;
      let preview: HTMLCanvasElement | null = null;
      try {
        const { canvas, analysis } = normalized;
        const frame = artworkThumbnailFrame(analysis.bounds, canvas.width, canvas.height, safeMargin);
        output = document.createElement('canvas');
        output.width = frame.width;
        output.height = frame.height;
        const context = output.getContext('2d');
        if (!context) throw new Error('Thumbnail canvas is unavailable.');
        context.drawImage(
          canvas,
          frame.source.x, frame.source.y, frame.source.width, frame.source.height,
          frame.destination.x, frame.destination.y, frame.destination.width, frame.destination.height,
        );
        preview = downscale(output, maxEdge);
        return preview.toDataURL('image/png');
      } catch (cause) {
        throw asDesignAssetError(cause, 'design_normalize_failed', 'normalize', catalogAssetMetadata(url));
      } finally {
        normalized.canvas.width = 0; normalized.canvas.height = 0;
        if (output) { output.width = 0; output.height = 0; }
        if (preview) { preview.width = 0; preview.height = 0; }
      }
    });
    thumbnailCache.set(key, created);
    void created.catch(() => { if (thumbnailCache.get(key) === created) thumbnailCache.delete(key); });
    pending = created;
  }
  return pending;
}

export function isCoarsePointer(): boolean {
  return Boolean(window.matchMedia?.('(pointer: coarse)').matches) || /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}
